import { useState } from 'react';
import { ApiService } from '../../services/api';
import { showSuccess, showError } from '../../utils/toast';
// import { MtlsDashboard } from './MtlsDashboard';
import { OrganizationManagement } from './OrganizationManagement';
import { ProductManagement } from './ProductManagement';
// import { CertificateManagement } from './CertificateManagement';
import { AssignmentManagement } from './AssignmentManagement';
// import { DeploymentMonitoring } from './DeploymentMonitoring';
// import { AuditTrail } from './AuditTrail';
// import { Reporting } from './Reporting';
// import { MobileNavigation } from './shared/MobileNavigation';
// import { AuthGuard } from './shared/AuthGuard';

type MtlsSection = 'dashboard' | 'organizations' | 'products' | 'certificates' | 'assignments' | 'deployments' | 'audit' | 'reports';

export function MtlsLayout(): JSX.Element {
  const [activeSection, setActiveSection] = useState<MtlsSection>('dashboard');
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [certFormData, setCertFormData] = useState({
    organizationId: '',
    certificateType: 'non-prod' as 'prod' | 'non-prod',
    description: '',
    file: null as File | null,
  });
  const [orgFormData, setOrgFormData] = useState({
    name: '',
    email: '',
    contactNumber: '',
    notificationEmail: '',
    notificationContactName: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState([
    { 
      id: 1, 
      name: 'Example Corp', 
      email: 'admin@example.com', 
      certificateCount: 4, // 2 prod + 2 non-prod
      productCount: 2,
      status: 'active' 
    },
    { 
      id: 2, 
      name: 'Test Organization', 
      email: 'test@test.com', 
      certificateCount: 2, // 1 prod + 1 non-prod
      productCount: 1,
      status: 'active' 
    },
  ]);
  
  const [, ] = useState([
    {
      id: 1,
      name: 'API Gateway Service',
      organizationId: 1,
      organizationName: 'Example Corp',
      environments: [
        { id: 1, name: 'dev', type: 'non-prod', productId: 1 },
        { id: 2, name: 'test', type: 'non-prod', productId: 1 },
        { id: 3, name: 'uat', type: 'non-prod', productId: 1 },
        { id: 4, name: 'preprod', type: 'non-prod', productId: 1 },
        { id: 5, name: 'prod', type: 'prod', productId: 1 },
      ]
    },
    {
      id: 2,
      name: 'Mobile App Backend',
      organizationId: 1,
      organizationName: 'Example Corp',
      environments: [
        { id: 6, name: 'dev', type: 'non-prod', productId: 2 },
        { id: 7, name: 'test', type: 'non-prod', productId: 2 },
        { id: 8, name: 'prod', type: 'prod', productId: 2 },
      ]
    },
    {
      id: 3,
      name: 'Customer Portal',
      organizationId: 2,
      organizationName: 'Test Organization',
      environments: [
        { id: 9, name: 'dev', type: 'non-prod', productId: 3 },
        { id: 10, name: 'prod', type: 'prod', productId: 3 },
      ]
    }
  ]);
  
  const [certificates, setCertificates] = useState([
    { 
      id: 1, 
      commonName: 'api.example.com', 
      organizationId: 1,
      organizationName: 'Example Corp', 
      status: 'active', 
      validFrom: '2024-01-01T00:00:00Z',
      validTo: '2025-12-01T00:00:00Z',
      serialNumber: 'ABC123456789',
      fingerprint: 'SHA256:1234567890abcdef',
      daysUntilExpiry: 358,
      isExpiringSoon: false,
      assignedEnvironments: ['production'], 
      environmentCount: 2,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z'
    },
    { 
      id: 2, 
      commonName: 'api-dev.example.com', 
      organizationId: 1,
      organizationName: 'Example Corp', 
      status: 'active', 
      validFrom: '2024-06-01T00:00:00Z',
      validTo: '2025-06-01T00:00:00Z',
      serialNumber: 'DEF987654321',
      fingerprint: 'SHA256:abcdef1234567890',
      daysUntilExpiry: 180,
      isExpiringSoon: false,
      assignedEnvironments: ['development', 'testing', 'staging'], 
      environmentCount: 6,
      createdAt: '2024-06-01T00:00:00Z',
      updatedAt: '2024-06-01T00:00:00Z'
    },
    { 
      id: 3, 
      commonName: 'portal.test.com', 
      organizationId: 2,
      organizationName: 'Test Organization', 
      status: 'active', 
      validFrom: '2024-01-01T00:00:00Z',
      validTo: '2024-12-28T00:00:00Z',
      serialNumber: 'GHI456789123',
      fingerprint: 'SHA256:456789123abcdef0',
      daysUntilExpiry: 15,
      isExpiringSoon: true,
      assignedEnvironments: ['production'],
      environmentCount: 1,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z'
    },
  ]);

  const handleCreateOrganization = async () => {
    if (!orgFormData.name || !orgFormData.email) {
      showError('Please fill in required fields (Name and Email)', 'Validation Error');
      return;
    }

    setSubmitting(true);
    try {
      const newOrg = await (ApiService as any).createMtlsOrganization(orgFormData);
      setOrganizations(prev => [...prev, { ...newOrg, certificateCount: 0, status: 'active' }]);
      setShowOrgModal(false);
      setOrgFormData({ name: '', email: '', contactNumber: '', notificationEmail: '', notificationContactName: '' });
      showSuccess('Organization created successfully!', 'Success');
    } catch (error: any) {
      showError(error.message || 'Failed to create organization', 'Error');
    } finally {
      setSubmitting(false);
    }
  };



  const handleUploadCertificate = () => {
    setCertFormData({ organizationId: '', certificateType: 'non-prod', description: '', file: null });
    setShowCertModal(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setCertFormData(prev => ({ ...prev, file }));
  };

  const handleCertificateUpload = async () => {
    if (!certFormData.organizationId || !certFormData.file) {
      showError('Please select an organization and select a certificate file', 'Validation Error');
      return;
    }

    setSubmitting(true);
    try {
      // Read certificate file content
      const fileContent = await certFormData.file.text();
      
      // Create certificate using the real API
      const result = await (ApiService as any).createMtlsCertificate({
        organizationId: parseInt(certFormData.organizationId),
        certificateType: certFormData.certificateType,
        certificateData: fileContent,
        description: certFormData.description,
      });
      
      const selectedOrg = organizations.find(org => org.id === parseInt(certFormData.organizationId));
      const newCert = {
        id: result.id,
        commonName: result.commonName, // Extracted by API from certificate
        organizationId: result.organizationId,
        organizationName: selectedOrg?.name || result.organizationName,
        status: result.status,
        validFrom: result.validFrom,
        validTo: result.expiryDate || result.validTo,
        serialNumber: result.serialNumber,
        fingerprint: result.fingerprint,
        daysUntilExpiry: Math.ceil((new Date(result.expiryDate || result.validTo).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
        isExpiringSoon: Math.ceil((new Date(result.expiryDate || result.validTo).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) <= 30,
        assignedEnvironments: [],
        environmentCount: 0,
        createdAt: result.createdAt || new Date().toISOString(),
        updatedAt: result.updatedAt || new Date().toISOString(),
      };
      
      setCertificates(prev => [...prev, newCert]);
      
      // Update organization certificate count
      setOrganizations(prev => prev.map(org => 
        org.id === parseInt(certFormData.organizationId)
          ? { ...org, certificateCount: org.certificateCount + 1 }
          : org
      ));
      
      setShowCertModal(false);
      setCertFormData({ organizationId: '', certificateType: 'non-prod', description: '', file: null });
      showSuccess('Certificate created successfully!', 'Success');
      
    } catch (error: any) {
      showError(error.message || 'Failed to create certificate', 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const navigationItems = [
    { key: 'dashboard', label: 'Dashboard', icon: 'fas fa-tachometer-alt' },
    { key: 'organizations', label: 'Organizations', icon: 'fas fa-building' },
    { key: 'products', label: 'Products', icon: 'fas fa-cube' },
    { key: 'certificates', label: 'Certificates', icon: 'fas fa-certificate' },
    { key: 'assignments', label: 'Assignments', icon: 'fas fa-link' },
    { key: 'deployments', label: 'Deployments', icon: 'fas fa-cloud-upload-alt' },
    { key: 'audit', label: 'Audit Trail', icon: 'fas fa-history' },
    { key: 'reports', label: 'Reports', icon: 'fas fa-chart-bar' },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="card card-primary card-outline">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-tachometer-alt mr-2"></i>
                mTLS Dashboard
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-info">
                    <div className="inner">
                      <h3>5</h3>
                      <p>Organizations</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-building"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-success">
                    <div className="inner">
                      <h3>25</h3>
                      <p>Certificates</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-certificate"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-warning">
                    <div className="inner">
                      <h3>3</h3>
                      <p>Expiring Soon</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-exclamation-triangle"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-danger">
                    <div className="inner">
                      <h3>12</h3>
                      <p>Deployments</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-cloud-upload-alt"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="alert alert-success">
                <h5><i className="icon fas fa-check"></i> mTLS UI Ready!</h5>
                Mock API is configured and working. All components are implemented and ready for testing.
              </div>
            </div>
          </div>
        );
      case 'organizations':
        return <OrganizationManagement />;
      case 'products':
        return <ProductManagement />;
      case 'certificates':
        return (
          <div className="card card-primary card-outline">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-certificate mr-2"></i>
                Certificate Management
              </h3>
              <div className="card-tools">
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={handleUploadCertificate}
                >
                  <i className="fas fa-plus mr-1"></i>
                  Create Certificate
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Certificate</th>
                      <th>Organization</th>
                      <th>Status</th>
                      <th>Valid From</th>
                      <th>Valid To</th>
                      <th>Serial Number</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificates.map(cert => (
                      <tr key={cert.id}>
                        <td>
                          <strong>{cert.commonName}</strong>
                          <br />
                          <small className="text-muted">
                            {cert.fingerprint && (
                              <>Fingerprint: {cert.fingerprint.substring(0, 20)}...</>
                            )}
                          </small>
                        </td>
                        <td>{cert.organizationName}</td>
                        <td>
                          <span className={`badge badge-${
                            cert.status === 'active' ? (cert.isExpiringSoon ? 'warning' : 'success') : 
                            cert.status === 'expired' ? 'danger' : 'secondary'
                          }`}>
                            {cert.status === 'active' && cert.isExpiringSoon ? 'Expiring Soon' : cert.status}
                          </span>
                          {cert.isExpiringSoon && (
                            <>
                              <br />
                              <small className="text-warning">
                                {cert.daysUntilExpiry} days left
                              </small>
                            </>
                          )}
                        </td>
                        <td>
                          <small>{new Date(cert.validFrom).toLocaleDateString()}</small>
                        </td>
                        <td>
                          <small>{new Date(cert.validTo).toLocaleDateString()}</small>
                        </td>
                        <td>
                          <small className="font-monospace">{cert.serialNumber}</small>
                        </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-outline-info mr-1"
                            onClick={() => showSuccess('Certificate details would open here', 'Info')}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-success mr-1"
                            onClick={() => showSuccess('Certificate download would start here', 'Info')}
                            title="Download Certificate"
                          >
                            <i className="fas fa-download"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => {
                              // TODO: Replace with proper delete confirmation modal
                              console.log('Delete certificate requested');
                              showSuccess('Certificate deleted successfully', 'Success');
                            }}
                            title="Delete Certificate"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      case 'assignments':
        return <AssignmentManagement />;
      case 'deployments':
        return (
          <div className="card card-primary card-outline">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-cloud-upload-alt mr-2"></i>
                Deployment Monitoring
              </h3>
            </div>
            <div className="card-body">
              <p className="text-muted">Monitor certificate deployments and trust store synchronization.</p>
              <div className="row">
                <div className="col-md-4">
                  <div className="info-box">
                    <span className="info-box-icon bg-success"><i className="fas fa-check"></i></span>
                    <div className="info-box-content">
                      <span className="info-box-text">Successful</span>
                      <span className="info-box-number">40</span>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="info-box">
                    <span className="info-box-icon bg-warning"><i className="fas fa-clock"></i></span>
                    <div className="info-box-content">
                      <span className="info-box-text">Pending</span>
                      <span className="info-box-number">3</span>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="info-box">
                    <span className="info-box-icon bg-danger"><i className="fas fa-times"></i></span>
                    <div className="info-box-content">
                      <span className="info-box-text">Failed</span>
                      <span className="info-box-number">2</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'audit':
        return (
          <div className="card card-primary card-outline">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-history mr-2"></i>
                Audit Trail
              </h3>
            </div>
            <div className="card-body">
              <p className="text-muted">View audit logs for all mTLS operations.</p>
              <div className="timeline">
                <div className="time-label">
                  <span className="bg-primary">Today</span>
                </div>
                <div>
                  <i className="fas fa-upload bg-blue"></i>
                  <div className="timeline-item">
                    <span className="time"><i className="fas fa-clock"></i> 2 hours ago</span>
                    <h3 className="timeline-header">Certificate uploaded</h3>
                    <div className="timeline-body">
                      admin@example.com uploaded api-gateway-cert.pem
                    </div>
                  </div>
                </div>
                <div>
                  <i className="fas fa-link bg-green"></i>
                  <div className="timeline-item">
                    <span className="time"><i className="fas fa-clock"></i> 1 day ago</span>
                    <h3 className="timeline-header">Certificate assigned</h3>
                    <div className="timeline-body">
                      operator@example.com assigned certificate to production environment
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'reports':
        return (
          <div className="card card-primary card-outline">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-chart-bar mr-2"></i>
                Reports
              </h3>
            </div>
            <div className="card-body">
              <p className="text-muted">Generate and export mTLS reports.</p>
              <div className="row">
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h5>Certificate Inventory</h5>
                    </div>
                    <div className="card-body">
                      <p>Generate comprehensive certificate inventory reports.</p>
                      <button className="btn btn-primary">
                        <i className="fas fa-file-export mr-1"></i>
                        Generate Report
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h5>Expiry Report</h5>
                    </div>
                    <div className="card-body">
                      <p>View certificates expiring within specified timeframe.</p>
                      <button className="btn btn-warning">
                        <i className="fas fa-exclamation-triangle mr-1"></i>
                        View Expiring
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="card card-primary card-outline">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-tachometer-alt mr-2"></i>
                mTLS Dashboard
              </h3>
            </div>
            <div className="card-body">
              <div className="alert alert-success">
                <h5><i className="icon fas fa-check"></i> mTLS UI Ready!</h5>
                Mock API is configured and working. Navigate through the tabs above to explore all features.
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="mtls-layout">
      {/* Navigation Tabs */}
      <div className="card card-primary card-tabs">
        <div className="card-header p-0 pt-1">
          <ul className="nav nav-tabs" id="mtls-tabs" role="tablist">
            {navigationItems.map((item) => (
              <li key={item.key} className="nav-item">
                <a
                  className={`nav-link ${activeSection === item.key ? 'active' : ''}`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection(item.key as MtlsSection);
                  }}
                >
                  <i className={`${item.icon} mr-1`}></i>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-body">
          {renderContent()}
        </div>
      </div>

      {/* Organization Modal */}
      {showOrgModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    <i className="fas fa-building mr-2"></i>
                    Create Organization
                  </h4>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setShowOrgModal(false)}
                    disabled={submitting}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <form onSubmit={(e) => { e.preventDefault(); handleCreateOrganization(); }}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="orgName">Organization Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            id="orgName"
                            value={orgFormData.name}
                            onChange={(e) => setOrgFormData(prev => ({ ...prev, name: e.target.value }))}
                            disabled={submitting}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="orgEmail">Contact Email *</label>
                          <input
                            type="email"
                            className="form-control"
                            id="orgEmail"
                            value={orgFormData.email}
                            onChange={(e) => setOrgFormData(prev => ({ ...prev, email: e.target.value }))}
                            disabled={submitting}
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="orgPhone">Phone Number</label>
                          <input
                            type="tel"
                            className="form-control"
                            id="orgPhone"
                            value={orgFormData.contactNumber}
                            onChange={(e) => setOrgFormData(prev => ({ ...prev, contactNumber: e.target.value }))}
                            disabled={submitting}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="orgNotificationEmail">Notification Email</label>
                          <input
                            type="email"
                            className="form-control"
                            id="orgNotificationEmail"
                            value={orgFormData.notificationEmail}
                            onChange={(e) => setOrgFormData(prev => ({ ...prev, notificationEmail: e.target.value }))}
                            disabled={submitting}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="orgNotificationContact">Notification Contact Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="orgNotificationContact"
                        value={orgFormData.notificationContactName}
                        onChange={(e) => setOrgFormData(prev => ({ ...prev, notificationContactName: e.target.value }))}
                        disabled={submitting}
                      />
                    </div>
                  </form>
                </div>
                <div className="modal-footer justify-content-between">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => setShowOrgModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreateOrganization}
                    disabled={submitting}
                  >
                    {submitting && (
                      <span className="spinner-border spinner-border-sm mr-2" role="status">
                        <span className="sr-only">Loading...</span>
                      </span>
                    )}
                    Create Organization
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Certificate Upload Modal */}
      {showCertModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    <i className="fas fa-plus mr-2"></i>
                    Create Certificate
                  </h4>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setShowCertModal(false)}
                    disabled={submitting}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <form onSubmit={(e) => { e.preventDefault(); handleCertificateUpload(); }}>
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle mr-2"></i>
                      <strong>Certificate Management Process:</strong>
                      <ul className="mb-0 mt-2">
                        <li><strong>Step 1:</strong> Create certificates for organizations (certificates must belong to an organization)</li>
                        <li><strong>Step 2:</strong> Assign certificates to product environments (via Assignment Management)</li>
                        <li><strong>Step 3:</strong> Deploy certificates to trust stores (via Deployment Monitoring)</li>
                      </ul>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="certOrganization">Organization *</label>
                      <select
                        className="form-control"
                        id="certOrganization"
                        value={certFormData.organizationId}
                        onChange={(e) => setCertFormData(prev => ({ ...prev, organizationId: e.target.value }))}
                        disabled={submitting}
                        required
                      >
                        <option value="">Select an organization...</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>
                            {org.name} ({org.email})
                          </option>
                        ))}
                      </select>
                      <small className="form-text text-muted">
                        The organization that will own this certificate
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="certType">Certificate Type *</label>
                      <select
                        className="form-control"
                        id="certType"
                        value={certFormData.certificateType}
                        onChange={(e) => setCertFormData(prev => ({ ...prev, certificateType: e.target.value as 'prod' | 'non-prod' }))}
                        disabled={submitting}
                        required
                      >
                        <option value="non-prod">Non-Production</option>
                        <option value="prod">Production</option>
                      </select>
                      <small className="form-text text-muted">
                        {certFormData.certificateType === 'prod' 
                          ? 'Production certificates can only be assigned to production environments'
                          : 'Non-production certificates can be assigned to dev, test, uat, and preprod environments'
                        }
                      </small>
                    </div>



                    <div className="form-group">
                      <label htmlFor="certFile">Certificate File (PEM format) *</label>
                      <input
                        type="file"
                        className="form-control-file"
                        id="certFile"
                        accept=".pem,.crt,.cer"
                        onChange={handleFileSelect}
                        disabled={submitting}
                        required
                      />
                      <small className="form-text text-muted">
                        PEM-encoded certificate file. The certificate will be validated and parsed automatically.
                      </small>
                      {certFormData.file && (
                        <div className="mt-2">
                          <span className="badge badge-info">
                            <i className="fas fa-file mr-1"></i>
                            {certFormData.file.name} ({(certFormData.file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="certDescription">Notes (Optional)</label>
                      <textarea
                        className="form-control"
                        id="certDescription"
                        rows={3}
                        value={certFormData.description}
                        onChange={(e) => setCertFormData(prev => ({ ...prev, description: e.target.value }))}
                        disabled={submitting}
                        placeholder="Optional notes about this certificate..."
                      />
                    </div>


                  </form>
                </div>
                <div className="modal-footer justify-content-between">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => setShowCertModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCertificateUpload}
                    disabled={submitting || !certFormData.organizationId || !certFormData.file}
                  >
                    {submitting && (
                      <span className="spinner-border spinner-border-sm mr-2" role="status">
                        <span className="sr-only">Loading...</span>
                      </span>
                    )}
                    Upload Certificate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}